import { Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { Action } from '@libraries/vuex-module-decorators/Action';
import StateCacheService from '@/app/src/services/cache/StateCacheService';
import {
	throwCacheErrorIfUnsupportedInClient,
	throwIfNotCachingUnsupportedInClient
} from '@app/types/errors/CacheError';
import store from '../store';

const moduleId = 'auth';

@Module({
	name: moduleId,
	store,
	dynamic: true,
	namespaced: true
})
export default class AuthStoreModule extends VuexModule {
	/**
	 * Current Contentful Management API accessToken
	 */
	public managementToken: string | null = null;

	@Action
	public async saveState(): Promise<void> {
		throwCacheErrorIfUnsupportedInClient();

		const authStateCacheService: StateCacheService = new StateCacheService('auth');
		await authStateCacheService.save('managementToken', this.managementToken);
	}

	@Action
	public async restoreState(): Promise<void> {
		throwCacheErrorIfUnsupportedInClient();

		const authStateCacheService: StateCacheService = new StateCacheService('auth');
		if ((await authStateCacheService.keys()).length > 0) {
			this.setManagementToken((await authStateCacheService.restore('managementToken')) || this.managementToken);
			return;
		}

		// Legacy support for all store modules stored in a single table

		const cache: StateCacheService = new StateCacheService();
		const cachedState: Record<string, any> | null = await cache.restore(moduleId);
		if (!cachedState) return;

		if (cachedState.managementToken) this.setManagementToken(cachedState.managementToken);
	}

	@Action
	public async saveStateIfSupported(): Promise<void> {
		return this.saveState().catch(throwIfNotCachingUnsupportedInClient);
	}

	@Action
	public async restoreStateIfSupported(): Promise<void> {
		return this.restoreState().catch(throwIfNotCachingUnsupportedInClient);
	}

	/**
	 * Logs in a user via a management token
	 */
	@Action
	public async login(managementToken: string): Promise<void> {
		this.setManagementToken(managementToken);
	}

	/**
	 * Logs out the current user
	 */
	@Action
	public logout(): void {
		this.setManagementToken(null);
	}

	@Mutation
	protected setManagementToken(managementToken: string | null): void {
		this.managementToken = managementToken;
	}

	public get isLoggedIn(): boolean {
		return !!this.managementToken;
	}
}
